import { createClient } from '@supabase/supabase-js';
import type {
  Customer,
  CustomerInsert,
  CustomerUpdate,
  Contact,
  ContactInsert,
  ContactUpdate,
  Product,
  ProductInsert,
  ProductUpdate,
  Device,
  DeviceInsert,
  DeviceUpdate,
  Credential,
  CredentialInsert,
  CredentialUpdate,
  BillingRecord,
  BillingInsert,
  BillingUpdate,
  CustomerWithRelations,
  CustomerSummary,
} from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Check your .env file.');
}

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '');

// ============================================
// CUSTOMERS API
// ============================================
export const customersApi = {
  async getAll(): Promise<Customer[]> {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getSummaries(): Promise<CustomerSummary[]> {
    const { data, error } = await supabase
      .from('customer_summary')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<CustomerWithRelations | null> {
    const { data, error } = await supabase
      .from('customers')
      .select(`
        *,
        contacts (*),
        products (*),
        devices (*),
        credentials (*),
        billing_records (*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(customer: CustomerInsert): Promise<Customer> {
    const { data, error } = await supabase
      .from('customers')
      .insert(customer)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: CustomerUpdate): Promise<Customer> {
    const { data, error } = await supabase
      .from('customers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('customers')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// CONTACTS API
// ============================================
export const contactsApi = {
  async getByCustomer(customerId: string): Promise<Contact[]> {
    const { data, error } = await supabase
      .from('contacts')
      .select('*')
      .eq('customer_id', customerId)
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async create(contact: ContactInsert): Promise<Contact> {
    const { data, error } = await supabase
      .from('contacts')
      .insert(contact)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: ContactUpdate): Promise<Contact> {
    const { data, error } = await supabase
      .from('contacts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// PRODUCTS API
// ============================================
export const productsApi = {
  async getByCustomer(customerId: string): Promise<Product[]> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('customer_id', customerId)
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getAll(): Promise<Product[]> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async create(product: ProductInsert): Promise<Product> {
    const { data, error } = await supabase
      .from('products')
      .insert(product)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: ProductUpdate): Promise<Product> {
    const { data, error } = await supabase
      .from('products')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// DEVICES API
// ============================================
export const devicesApi = {
  async getByCustomer(customerId: string): Promise<Device[]> {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .eq('customer_id', customerId)
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getAll(): Promise<Device[]> {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async create(device: DeviceInsert): Promise<Device> {
    const { data, error } = await supabase
      .from('devices')
      .insert(device)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: DeviceUpdate): Promise<Device> {
    const { data, error } = await supabase
      .from('devices')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('devices')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// CREDENTIALS API
// ============================================
export const credentialsApi = {
  async getByCustomer(customerId: string): Promise<Credential[]> {
    const { data, error } = await supabase
      .from('credentials')
      .select('*')
      .eq('customer_id', customerId)
      .order('system_name');

    if (error) throw error;
    return data || [];
  },

  async create(credential: CredentialInsert): Promise<Credential> {
    const { data, error } = await supabase
      .from('credentials')
      .insert(credential)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: CredentialUpdate): Promise<Credential> {
    const { data, error } = await supabase
      .from('credentials')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('credentials')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// BILLING API
// ============================================
export const billingApi = {
  async getByCustomer(customerId: string): Promise<BillingRecord[]> {
    const { data, error } = await supabase
      .from('billing_records')
      .select('*')
      .eq('customer_id', customerId)
      .order('month', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async create(billing: BillingInsert): Promise<BillingRecord> {
    const { data, error } = await supabase
      .from('billing_records')
      .insert(billing)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: BillingUpdate): Promise<BillingRecord> {
    const { data, error } = await supabase
      .from('billing_records')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('billing_records')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// SETTINGS API
// ============================================
export const settingsApi = {
  async get(key: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', key)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return data?.value || null;
  },

  async set(key: string, value: string | null): Promise<void> {
    const { error } = await supabase
      .from('settings')
      .upsert({ key, value }, { onConflict: 'key' });

    if (error) throw error;
  },
};

// ============================================
// DASHBOARD STATS API
// ============================================
export const dashboardApi = {
  async getStats() {
    const [customersResult, productsResult, devicesResult, billingResult] = await Promise.all([
      supabase.from('customers').select('id', { count: 'exact' }),
      supabase.from('products').select('cost, status'),
      supabase.from('devices').select('id', { count: 'exact' }),
      supabase.from('billing_records').select('status'),
    ]);

    const totalCustomers = customersResult.count || 0;
    const products = productsResult.data || [];
    const totalDevices = devicesResult.count || 0;
    const billingRecords = billingResult.data || [];

    const mrr = products
      .filter((p) => p.status === 'Active')
      .reduce((sum, p) => sum + (p.cost || 0), 0);

    const overdueInvoices = billingRecords.filter((b) => b.status === 'Overdue').length;

    return {
      totalCustomers,
      totalDevices,
      mrr,
      overdueInvoices,
      activeProducts: products.filter((p) => p.status === 'Active').length,
    };
  },
};
